import { DeleteParticipantService } from "app/core/services/administration/delete-participant/delete-participant.service";
import { GetParticipantsService } from "app/core/services/administration/get-participants/get-participants.service";
import { AddParticipantService } from "app/core/services/administration/add-participant/add-participant.service";
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Participant } from "app/core/models/participant";
import { FormsModule } from "@angular/forms";
import Modal from 'bootstrap/js/dist/modal';
import { Toast } from 'bootstrap';

@Component({
    selector: 'app-administration',
    standalone: true,
    imports: [ TranslateModule, FormsModule ],
    templateUrl: './administration.component.html',
    styleUrl: './administration.component.scss'
})

export class AdministrationComponent implements AfterViewInit, OnInit {

    @ViewChild('administrationSearcher') administrationSearcher!: ElementRef<HTMLInputElement>;

	@ViewChild('toastElement', { static: true }) toastEl!: ElementRef;
	toastMessage: string = '';

    participants: Participant[] = [];
	filteredParticipants: Participant[] = [];

    isLoading = false;

    sortCriteria: string = '';
	sortDirection: 'asc' | 'desc' = 'asc';

	selectedParticipant: Participant | null = null;

	newParticipantID: string = "";
	newParticipantName: string = "";
	newParticipantTargetURL: string = "";
	newParticipantProtocols: string[] = [];

	wrongNewParticipantIDField: boolean = false;
	wrongNewParticipantNameField: boolean = false;
	wrongNewParticipantTargetURLField: boolean = false;
	wrongNewParticipantProtocolsField: boolean = false;
	showProtocolsDropdown: boolean = false;

	protocol: string = '';
	protocols: string[] = [];

	@ViewChild('addParticipantModal') addParticipantModalRef!: ElementRef;
	public addParticipantModal!: Modal;

	@ViewChild('deleteParticipantModal') deleteParticipantModalRef!: ElementRef;
	public deleteParticipantModal!: Modal;

    constructor(
		private deleteParticipantService: DeleteParticipantService,
        private getParticipantsService: GetParticipantsService,
		private addParticipantService: AddParticipantService,
        private translateService: TranslateService
    ) { }

    ngOnInit(): void {
        this.getParticipants();
		this.fillProtocols();
        this.filteredParticipants = [...this.participants];
    }

	ngAfterViewInit(): void {
		this.addParticipantModal = new Modal(this.addParticipantModalRef.nativeElement);
		this.deleteParticipantModal = new Modal(this.deleteParticipantModalRef.nativeElement);
	}

    // ---------------- SEARCH ---------------- //

    search(): void {
        const term = this.administrationSearcher.nativeElement.value.toLowerCase();

		if (!term) {
			this.filteredParticipants = [...this.participants];
			this.applySorting();
			return;
		}

        this.filteredParticipants = this.filteredParticipants.filter(participant =>
            participant.ID.toLowerCase().includes(term) ||
            participant.name.toLowerCase().includes(term) ||
            participant.targetURL.toLowerCase().includes(term)
        );
    }

    // ---------------- PARTICIPANTS ---------------- //

    private getParticipants(): void {
        this.isLoading = true;
        this.getParticipantsService.obtain$().subscribe({
            next: ( participants ) => {
                this.participants = participants;
                this.filteredParticipants = [...this.participants];
                this.isLoading = false;
            },
            error: (err) => {
                console.error('[AdministrationComponent] Error:', err.message);
                const msg = 'Error al obtener los participantes';
                this.showToast(msg, 'danger');
                this.isLoading = false;
            }
        });
    }

	private fillProtocols(): void {
		this.protocols = [
			'dataspace-protocol-http'
		]
	}

    // ---------------- SORT ---------------- //

    sortParticipantsBy(criteria: string): void {

		if (this.sortCriteria === criteria) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortDirection = 'asc';
		}

		this.sortCriteria = criteria;

		switch (criteria) {
			case 'id':
				this.filteredParticipants.sort((a, b) =>
				this.sortDirection === 'asc'
					? this.naturalCompare(a.ID, b.ID)
					: this.naturalCompare(b.ID, a.ID)
				);
				break;
			case 'name':
				this.filteredParticipants.sort((a, b) =>
				this.sortDirection === 'asc'
					? this.naturalCompare(a.name, b.name)
					: this.naturalCompare(b.name, a.name)
				);
				break;
            case 'targetURL':
				this.filteredParticipants.sort((a, b) =>
				this.sortDirection === 'asc'
					? this.naturalCompare(a.targetURL, b.targetURL)
					: this.naturalCompare(b.targetURL, a.targetURL)
				);
				break;
			default:
				const msg = this.translateService.instant('ADMINISTRATION.CRITERIA');
				this.showToast(msg, 'warning');
				break;
		}

	}

    private naturalCompare(a: string, b: string): number {

		const ax = a.split(/(\d+)/).filter(s => s.length > 0),
			  bx = b.split(/(\d+)/).filter(s => s.length > 0);

		while (ax.length > 0 && bx.length > 0) {
			const aPart = ax.shift()!;
			const bPart = bx.shift()!;
			const aIsNum = !isNaN(Number(aPart));
			const bIsNum = !isNaN(Number(bPart));

			if (aIsNum && bIsNum) {
				const aNum = Number(aPart);
				const bNum = Number(bPart);
				if (aNum !== bNum) return aNum - bNum;
			} else if (aPart !== bPart) {
				return aPart.localeCompare(bPart);
			}
		}

		return ax.length - bx.length;

	}

	private applySorting(): void {

		if (!this.sortCriteria) return;

		switch (this.sortCriteria) {
			case 'id':
				this.filteredParticipants.sort((a, b) =>
				this.sortDirection === 'asc'
					? a.ID.localeCompare(b.ID)
					: b.ID.localeCompare(a.ID)
				);
				break;
			case 'name':
				this.filteredParticipants.sort((a, b) =>
				this.sortDirection === 'asc'
					? a.name.localeCompare(b.name)
					: b.name.localeCompare(a.name)
				);
				break;
            case 'targetURL':
				this.filteredParticipants.sort((a, b) =>
				this.sortDirection === 'asc'
					? a.targetURL.localeCompare(b.targetURL)
					: b.targetURL.localeCompare(a.targetURL)
				);
				break;
		}

	}

    // ---------------- ADD PARTICIPANT ---------------- //

    openAddParticipant(): void {
		this.addParticipantModal.show();
    }

	cancelAddParticipant(): void {
		this.cleanData();
		this.addParticipantModal.hide();
    }

	addParticipant(): void {

		this.validateInputs();

		if (!this.areInputsValid()) { return; }

		this.addParticipantService.addParticipant$(
			this.newParticipantID,
			this.newParticipantName,
			this.newParticipantTargetURL,
			this.newParticipantProtocols
		).subscribe({
			next: (participant) => {
				this.participants = [participant, ...this.participants];
				this.filteredParticipants = [...this.participants];
				this.applySorting();
				const msg = this.translateService.instant('ADMINISTRATION.SUCCESS.CREATED');
				this.showToast(msg, 'success');
				this.addParticipantModal.hide();
				this.cleanData();
			},
			error: (err) => {
				console.error('[addParticipant] Error:', err);
				const msg = this.translateService.instant('ADMINISTRATION.ERROR.CREATED');
				this.showToast(msg, 'danger');
			}
		});

	}

	private validateInputs(): void {
		this.wrongNewParticipantIDField = 
			this.newParticipantID.length < 1 || 
			this.newParticipantID.length > 50;
		this.wrongNewParticipantNameField =
			this.newParticipantName.length < 1 ||
			this.newParticipantName.length > 50;
		this.wrongNewParticipantTargetURLField =
			this.newParticipantTargetURL.length < 1 ||
			this.newParticipantTargetURL.length > 100;
		this.wrongNewParticipantProtocolsField =
			this.newParticipantProtocols.length === 0
	}

	private areInputsValid(): boolean {
		return !this.wrongNewParticipantIDField && !this.wrongNewParticipantNameField &&
			   !this.wrongNewParticipantTargetURLField && !this.wrongNewParticipantProtocolsField;
	}

	private cleanData(): void {
		this.newParticipantID = "";
		this.newParticipantName = "";
		this.newParticipantTargetURL = "";
		this.newParticipantProtocols = [];

		this.wrongNewParticipantIDField = false;
		this.wrongNewParticipantNameField = false;
		this.wrongNewParticipantTargetURLField = false;
		this.wrongNewParticipantProtocolsField = false;

		this.showProtocolsDropdown = false;
	}

	addProtocol(): void {

		if (!this.protocol?.trim()) { return; }

		const newProtocol = this.protocol.trim().toLowerCase();
		const alreadyExists = this.newParticipantProtocols.some(
			p => p.trim().toLowerCase() === newProtocol
		);
		if (alreadyExists) {
			const msg = this.translateService.instant('ADMINISTRATION.PROTOCOL-DUPLICATED');
			this.showToast(msg, 'warning');
			return;
		}

		this.newParticipantProtocols.push(this.protocol);

		this.wrongNewParticipantProtocolsField = false;

		this.protocol = '';

	}

	removeProtocol(index: number): void {
		if (index >= 0 && index < this.newParticipantProtocols.length) {
			this.newParticipantProtocols.splice(index, 1);
		}
	}

	toggleDropdown(): void {
		this.showProtocolsDropdown = !this.showProtocolsDropdown;
	}

	toggleProtocol(protocol: string): void {
		const index = this.newParticipantProtocols.indexOf(protocol);
		index === -1
			? this.newParticipantProtocols.push(protocol)
			: this.newParticipantProtocols.splice(index, 1);
	}

	isSelected(protocol: string): boolean {
		return this.newParticipantProtocols.some(a => a === protocol);
	}

	// ---------------- DELETE PARTICIPANT ---------------- //

    openDeleteParticipant(participant: Participant): void {
		this.selectedParticipant = participant;
		this.deleteParticipantModal.show();		
    }

	cancelDeleteParticipant(): void {
		this.selectedParticipant = null;
		this.deleteParticipantModal.hide();	
	}

	deleteParticipant(): void {

		if (!this.selectedParticipant) {
			console.warn('No participant selected');
			return;
		}

		this.deleteParticipantService.deleteParticipant$(this.selectedParticipant.ID).subscribe({
			next: (deleted) => {
				if (deleted) {
					this.participants = this.participants.filter(p => p.ID !== this.selectedParticipant!.ID);
					this.filteredParticipants = this.filteredParticipants.filter(p => p.ID !== this.selectedParticipant!.ID);
					this.applySorting();
					const msg = this.translateService.instant('ADMINISTRATION.SUCCESS.DELETED');
					this.showToast(msg, 'success');
					this.selectedParticipant = null;
					this.deleteParticipantModal.hide();
				}
			},
			error: (err) => {
				console.error('[deleteParticipant] Error:', err);
				const msg = this.translateService.instant('ADMINISTRATION.ERROR.DELETED');
				this.showToast(msg, 'danger');
			}
		});
		
	}

	// ---------------- TOAST ---------------- //

	showToast(message: string, type: 'success' | 'danger' | 'warning' = 'success') {
		this.toastMessage = message;
		const el = this.toastEl.nativeElement as HTMLElement;
		el.classList.remove('text-bg-success', 'text-bg-danger', 'text-bg-warning');
		el.classList.add(`text-bg-${type}`);
		const bsToast = new Toast(el);
		bsToast.show();
	}

}